import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { NotificationType } from '../types/NotificationType'

type NotificationsManagerContextType = {
    postNotification: (notificationType: NotificationType, data?: any) => void
    notificationData: {
        [notificationType: string]: any
    }
    getNotificationData: (notificationType: NotificationType) => any
    registerNotificationListener: (
        notificationType: NotificationType,
        callback: (data: any) => void,
        id?: string
    ) => () => void
}

const NotificationsManagerContext = createContext<NotificationsManagerContextType>(null!)

export const NotificationsManagerProvider = ({ children }: { children: React.ReactNode }) => {
    const [notificationListeners, setNotificationListeners] = useState<{
        [notificationType: string]: { [listenerID: string]: (data?: any) => void }
    }>({})
    const [notificationData, setNotificationData] = useState<{
        [notificationType: string]: any
    }>({})
    const notificationDataRef = useRef(notificationData)
    notificationDataRef.current = notificationData

    const registerNotificationListener = useCallback(
        (notificationType: NotificationType, callback: (data: any) => void, id?: string) => {
            const listenerID = id ?? Math.random().toString()
            setNotificationListeners((previousListeners) => ({
                ...previousListeners,
                [notificationType]: {
                    ...(previousListeners[notificationType] ?? {}),
                    [listenerID]: callback,
                },
            }))
            return () => {
                setNotificationListeners((previousListeners) => {
                    const { [listenerID]: _, ...rest } = previousListeners[notificationType]
                    return { ...previousListeners, [notificationType]: rest }
                })
            }
        },
        []
    )

    const postNotification = useCallback(
        (notificationType: NotificationType, data?: any) => {
            setNotificationData((previousEvents) => ({
                ...previousEvents,
                [notificationType]: data,
            }))
            Object.values(notificationListeners[notificationType] ?? {}).forEach((callback) => {
                callback(data)
            })
        },
        [notificationListeners]
    )

    const getNotificationData = useCallback((notificationType: NotificationType) => {
        return notificationDataRef.current[notificationType]
    }, [])

    // The RecordFactory requires access to the `postNotification` method, but RF is instantiated
    // manually by each caller. We don't want to have to pass in the postNotification function
    // at each instantiation point, so for now we're using this massive hack to just stick
    // the function on a known place on `window`, which the RF then knows to grab in it's constructor.
    //
    // Eventually, the right play is probably to grab a RF instantiation from a hook like `useRecordFactory`.
    // That hook could then DI the `postNotification` function.
    useEffect(() => {
        ;(window as any).pocketsuite_postNotification = postNotification
    }, [postNotification])

    const value = useMemo(
        () => ({
            postNotification,
            notificationData,
            getNotificationData,
            registerNotificationListener,
        }),
        [postNotification, notificationData, getNotificationData, registerNotificationListener]
    )

    return (
        <NotificationsManagerContext.Provider value={value}>
            {children}
        </NotificationsManagerContext.Provider>
    )
}

export const useNotificationsManagerContext = () => {
    return useContext(NotificationsManagerContext)
}

export const useNotificationListener = (
    notificationType: NotificationType,
    callback: (data: any) => void,
    id?: string
) => {
    const { registerNotificationListener } = useNotificationsManagerContext()

    const callbackRef = useRef(callback)
    callbackRef.current = callback

    useEffect(() => {
        const deregister = registerNotificationListener(
            notificationType,
            (data) => callbackRef.current(data),
            id
        )
        return () => {
            deregister()
        }
    }, [notificationType, registerNotificationListener, id])
}
